/** Per-user interface preferences — pinned lead-drawer tabs today, whatever
 * else wants to follow a person between devices later.
 *
 * Stored in profiles.ui_prefs (MIGRATION_CRM2_PHASE30_UI_PREFS.sql), which is
 * per-USER. Deliberately NOT rec_org_settings — that is org-wide, so pinning a
 * tab there would repin it for the whole team. Deliberately NOT notif_prefs
 * either: the notifications page replaces that key wholesale, so anything
 * parked alongside it gets deleted on the first notification toggle.
 *
 * DEGRADES GRACEFULLY. Until the migration is applied the column does not
 * exist and the read 400s — so this falls back to localStorage and the feature
 * still works, just per-device. Once the column is there, the cached local copy
 * seeds the first sync and the server becomes the source of truth. No flag to
 * flip, nothing to remember.
 */
import { SUPABASE_URL, SUPABASE_ANON_KEY } from "../../config";
import { http } from "../../lib/http";

const LOCAL_KEY = "ui_prefs_v1";

export interface UiPrefs {
  /** lead-drawer tab ids the user pinned to the front of the strip */
  leadTabs?: string[];
  /** deal-drawer tab ids the user pinned. Separate key: the two drawers have
   *  different tab sets, and pinning "Rehab" in a deal must not try to pin a
   *  lead tab that does not exist. */
  dealTabs?: string[];
}

function readLocal(): UiPrefs {
  try {
    const raw = localStorage.getItem(LOCAL_KEY);
    const v: unknown = raw ? JSON.parse(raw) : null;
    return v !== null && typeof v === "object" && !Array.isArray(v) ? (v as UiPrefs) : {};
  } catch {
    return {};
  }
}

function writeLocal(p: UiPrefs): void {
  try { localStorage.setItem(LOCAL_KEY, JSON.stringify(p)); } catch { /* private mode */ }
}

/** Server copy if the column exists, else the local one. Never throws: a
 *  preference read must not be able to break the page it decorates. */
export async function loadUiPrefs(token: string | null, userId: string | null): Promise<UiPrefs> {
  const local = readLocal();
  if (!token || !userId) return local;
  try {
    const rows = await http<{ ui_prefs?: unknown }[]>(
      `${SUPABASE_URL}/rest/v1/profiles?id=eq.${encodeURIComponent(userId)}&select=ui_prefs`,
      { headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${token}` }, timeoutMs: 12000 },
    );
    const v = rows?.[0]?.ui_prefs;
    if (v !== null && typeof v === "object" && !Array.isArray(v)) {
      const server = v as UiPrefs;
      // Empty server value + something local = this device has prefs the
      // column has never seen (first run after the migration). Keep the local
      // ones; the next save pushes them up.
      return Object.keys(server).length ? server : local;
    }
    return local;
  } catch {
    return local;                       // column missing, offline, RLS — all fine
  }
}

/** Write local first so the UI is instant and survives a failed sync, then
 *  best-effort to the server. Returns true if the server copy landed. */
export async function saveUiPrefs(token: string | null, userId: string | null, next: UiPrefs): Promise<boolean> {
  writeLocal(next);
  if (!token || !userId) return false;
  try {
    await http(
      `${SUPABASE_URL}/rest/v1/profiles?id=eq.${encodeURIComponent(userId)}`,
      {
        method: "PATCH",
        headers: {
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Prefer: "return=minimal",
        },
        body: JSON.stringify({ ui_prefs: next }),
        timeoutMs: 12000,
      },
    );
    return true;
  } catch {
    return false;                        // stays local-only until the migration lands
  }
}
